import React, { forwardRef, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import { BoxArrowRight as FaSignOutAlt, Person } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { signOut } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrentAuthUser } from '../../utils/currentAuthUser';
import styles from './UserMenu.module.scss';

function getInitials(displayName?: string | null, email?: string | null): string {
  const name = (displayName || '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const mail = (email || '').trim();
  if (mail) return mail.slice(0, 2).toUpperCase();
  return '';
}

const UserMenuToggle = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    compact?: boolean;
    bsPrefix?: string;
  }
>(({ children, className, compact, bsPrefix: _bsPrefix, ...props }, ref) => (
  <button
    type="button"
    ref={ref}
    className={`${styles.trigger} ${compact ? styles.triggerCompact : ''} ${
      className || ''
    }`}
    {...props}
  >
    {children}
  </button>
));
UserMenuToggle.displayName = 'UserMenuToggle';

interface UserMenuProps {
  compact?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ compact = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const current = getCurrentAuthUser();

  const email = current?.email || user?.email || '';
  const displayName = current?.displayName || user?.displayName || '';
  const initials = useMemo(
    () => getInitials(displayName, email),
    [displayName, email]
  );

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user && !current) return null;

  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        as={UserMenuToggle}
        compact={compact}
        aria-label="Menú de usuario"
      >
        <span className={styles.avatar} aria-hidden>
          {initials || <Person size={16} />}
        </span>
        {!compact && (
          <span className={styles.meta}>
            <span className={styles.name}>
              {displayName || email || 'Usuario'}
            </span>
          </span>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className={styles.menu}>
        <div className={styles.menuHeader}>
          <div className={styles.menuName}>
            {displayName || email || 'Usuario'}
          </div>
          {email ? <div className={styles.menuEmail}>{email}</div> : null}
        </div>
        <Dropdown.Divider />
        <Dropdown.Item
          onClick={() => void handleLogout()}
          className={styles.logoutItem}
        >
          <FaSignOutAlt className="me-2" aria-hidden />
          Cerrar Sesión
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default UserMenu;
